// Registration metadata umbrella. Consumes checkFirstSlot/checkForm/checkTicketing
// from registrationValidity.js and adds i18n-coupled alert metadata (datesAlert, formAlert, etc.).
// API umbrella mirror: api/src/helpers/registrationCompletion.helper.ts (boolean only, no i18n).

import {t} from "i18next";
import {truncate} from "@sfs/utils/stringUtilities";
import {checkFirstSlot, checkForm, checkTicketing} from "./registrationValidity";

export const getRegistrationMetadata = (registration, project) => {
  // No registration = no metadata
  if (!registration) return {};

  // Check availabilities, form and ticketing
  const firstSlotCheck = checkFirstSlot(registration);
  const formCheck = checkForm(registration, project);
  const ticketingCheck = checkTicketing(registration, project);

  // OK indicators
  const metadata = {
    firstSlotIsOk: firstSlotCheck.isOk,
    formIsOk: formCheck.isOk,
    ticketingIsOk: ticketingCheck.isOk,
  };
  // Set the global indicator
  metadata.everythingIsOk = metadata.firstSlotIsOk && metadata.formIsOk && metadata.ticketingIsOk;

  // Set the alerts for each validation
  metadata.datesAlert =
    registration.booked && !firstSlotCheck.firstSlotIsDefined
      ? t("registrations:registrationValidation.selectDatesToRegister")
      : firstSlotCheck.firstSlotEndIsBeforeBeginning
      ? t("registrations:registrationValidation.endCannotBeBeforeStart")
      : undefined;
  metadata.formAlert =
    registration.booked && !metadata.formIsOk
      ? `${t(
          "registrations:registrationValidation.someFormFieldsAreMandatory"
        )} ${formCheck.unfulfilledMandatoryFields.map((field) => truncate(field, 30)).join('", "')}`
      : undefined;
  metadata.ticketingAlert =
    registration.booked && !metadata.ticketingIsOk
      ? t("registrations:registrationValidation.ticketingIsMandatory")
      : undefined;

  return metadata;
};

export const getInitializedRegistration = (registration, project, initializeDatabaseMetadata) => {
  const validationFields = getRegistrationMetadata(registration, project);
  const newRegistration = {
    ...registration,
    ...validationFields, // instant validation fields, always up to date
    touched: !initializeDatabaseMetadata, // To tell if the registration has been touched and is not in sync anymore with database
  };
  if (initializeDatabaseMetadata) {
    newRegistration.inDatabase = validationFields; // To keep a copy of the saved state
  }
  return newRegistration;
};

export const getSessionSubscription = (registration, session) =>
  registration?.sessionsSubscriptions?.find((ss) => ss.session === session?._id);
